import React from 'react';
import { Link, useLocation } from 'react-router';
import {
  type BreadcrumbItem,
  mergeBreadcrumbTrail,
  resolveBreadcrumbs,
} from '../../config/breadcrumbConfig';
import { PageMetaFromHeading } from './PageMeta';

export interface PageBreadcrumbProps {
  pageTitle: string;
  description?: string;
  /** Extra segments between auto-resolved parents and the current page */
  items?: BreadcrumbItem[];
}

const Chevron: React.FC = () => (
  <svg
    className="stroke-current shrink-0 text-gray-400 dark:text-gray-500"
    width="16"
    height="16"
    viewBox="0 0 17 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M6.0765 12.667L10.2432 8.50033L6.0765 4.33366"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({
  pageTitle,
  description,
  items: manualParents,
}) => {
  const { pathname } = useLocation();
  const resolved = resolveBreadcrumbs(pathname);
  const { items, description: mergedDescription } = mergeBreadcrumbTrail(
    resolved,
    pageTitle,
    manualParents,
    description,
  );

  const trail = [...items, { label: pageTitle }];

  return (
    <>
      <PageMetaFromHeading pageTitle={pageTitle} description={mergedDescription} />
    <header className="mb-6 border-b border-gray-200 pb-5 dark:border-gray-800">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white/90 sm:text-2xl">
            {pageTitle}
          </h1>
          {mergedDescription && (
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              {mergedDescription}
            </p>
          )}
        </div>

        <nav aria-label="Breadcrumb" className="shrink-0">
          <ol className="flex flex-wrap items-center gap-1">
            {trail.map((crumb, index) => {
              const isLast = index === trail.length - 1;
              return (
                <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                  {index > 0 && <Chevron />}
                  {isLast || !crumb.to ? (
                    <span
                      className={
                        isLast
                          ? 'text-sm font-medium text-gray-800 dark:text-white/90'
                          : 'text-sm text-gray-500 dark:text-gray-400'
                      }
                      aria-current={isLast ? 'page' : undefined}
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      to={crumb.to}
                      className="text-sm text-gray-500 transition-colors hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </header>
    </>
  );
};

export default PageBreadcrumb;
